import * as React from 'react';
import {
  Box,
  Button,
  Typography,
  Modal,
  Grid,
  Card,
  CardMedia,
  CardActions,
  IconButton,
  TablePagination,
  TextField,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const API_URL = import.meta.env.VITE_API_URL;
const IMAGE_URL = import.meta.env.VITE_IMAGE_URL;

const MAX_SIZE_KB = 20480;

type MediaItem = {
  mediaId: number;
  title: string;
  imageUrl: string;
  Tags?: { tagId: number; name: string; color: string }[];
};

type ImagePickerValue =
  | { type: 'file'; file: File }
  | { type: 'media'; mediaId: number; url: string };

type ImagePickerProps = {
  title: string;
  value?: string;
  onChange: (value: ImagePickerValue) => void;
  maxDimension?: number;
  recommendedSize?: number;
};

export default function ImagePicker({
  title,
  value,
  onChange,
  maxDimension = 1200,
  recommendedSize = 1200,
}: ImagePickerProps) {
  const [imageUrl, setImageUrl] = React.useState<string | undefined>(value);
  const [open, setOpen] = React.useState(false);
  const [imageError, setImageError] = React.useState<string | null>(null);
  const [imageWarning, setImageWarning] = React.useState<string | null>(null);

  const [media, setMedia] = React.useState<MediaItem[]>([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(12);
  const [total, setTotal] = React.useState(0);

  const [search, setSearch] = React.useState('');
  const [allTags, setAllTags] = React.useState<any[]>([]);
  const [selectedTags, setSelectedTags] = React.useState<Set<number>>(
    new Set(),
  );

  React.useEffect(() => {
    setImageUrl(value || undefined);
  }, [value]);

  const loadTags = () => {
    fetch(`${API_URL}/tags`)
      .then((r) => r.json())
      .then((d) => setAllTags(d.data));
  };

  const fetchMedia = () => {
    const params = new URLSearchParams();
    params.append('page', String(page + 1));
    params.append('perPage', String(rowsPerPage));
    if (search) params.append('search', search);
    if (selectedTags.size > 0) {
      selectedTags.forEach((id) => params.append('tagIds', String(id)));
    }

    fetch(`${API_URL}/media?${params.toString()}`)
      .then((r) => r.json())
      .then((data) => {
        setMedia(data.data);
        setTotal(data.total);
      });
  };

  React.useEffect(() => {
    if (open) {
      fetchMedia();
      loadTags();
    }
  }, [open, page, rowsPerPage, search, selectedTags]);

  const handleFileChange = (file: File) => {
    setImageError(null);
    setImageWarning(null);

    if (file.size / 1024 > MAX_SIZE_KB) {
      setImageError(
        `Розмір файлу не повинен перевищувати ${MAX_SIZE_KB / 1024} МБ`,
      );
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      const { width, height } = img;

      if (width > maxDimension || height > maxDimension) {
        setImageError(
          `Розміри ${width}×${height}px перевищують максимум ${maxDimension}×${maxDimension}px`,
        );
        URL.revokeObjectURL(objectUrl);
        return;
      }

      if (width !== recommendedSize || height !== recommendedSize) {
        setImageWarning(
          `Розміри ${width}×${height}px. Рекомендовано ${recommendedSize}×${recommendedSize}px`,
        );
      }

      setImageUrl(objectUrl);
      onChange({ type: 'file', file });
    };

    img.src = objectUrl;
  };

  const handleSelectMedia = (item: MediaItem) => {
    const fullUrl = `${IMAGE_URL}${item.imageUrl}`;
    setImageUrl(fullUrl);
    setImageError(null);
    setImageWarning(null);
    onChange({ type: 'media', mediaId: item.mediaId, url: fullUrl });
    setOpen(false);
  };

  const toggleTag = (id: number) => {
    const next = new Set(selectedTags);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedTags(next);
    setPage(0);
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography>{title}</Typography>

      {imageUrl && !imageError ? (
        <img
          src={imageUrl}
          style={{
            width: 200,
            height: 200,
            objectFit: 'cover',
            display: 'block',
            marginBottom: 8,
          }}
        />
      ) : (
        <Box
          sx={{ width: 200, height: 200, border: '1px solid grey', mb: 1 }}
        />
      )}

      <Typography
        variant="caption"
        display="block"
        sx={{ mb: 1, color: 'text.secondary' }}
      >
        Рекомендоване {recommendedSize}×{recommendedSize}px · Максимум{' '}
        {maxDimension}×{maxDimension}px · До {MAX_SIZE_KB / 1024} МБ
      </Typography>

      {imageError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {imageError}
        </Alert>
      )}

      {imageWarning && !imageError && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {imageWarning}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button component="label" variant="contained">
          Завантажити фото
          <input
            hidden
            type="file"
            accept="image/*"
            onChange={(e) => {
              if (e.target.files?.[0]) handleFileChange(e.target.files[0]);
            }}
          />
        </Button>

        <Button variant="outlined" onClick={() => setOpen(true)}>
          Обрати з медіа
        </Button>
      </Box>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '80%',
            maxHeight: '80%',
            bgcolor: 'background.paper',
            p: 2,
            overflowY: 'auto',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Медіа-бібліотека</Typography>
            <IconButton onClick={() => setOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <TextField
            fullWidth
            size="small"
            placeholder="Пошук..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {allTags.map((t) => {
              const active = selectedTags.has(t.tagId);
              return (
                <Box
                  key={t.tagId}
                  onClick={() => toggleTag(t.tagId)}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 2,
                    bgcolor: t.color,
                    opacity: active ? 1 : 0.3,
                    cursor: 'pointer',
                  }}
                >
                  <Typography variant="caption">{t.name}</Typography>
                </Box>
              );
            })}
          </Box>

          <Grid container spacing={2}>
            {media.map((item) => (
              <Grid size={{ xs: 6, sm: 4, md: 3 }} key={item.mediaId}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <CardMedia
                    component="img"
                    sx={{ height: 140, objectFit: 'cover' }}
                    image={`${IMAGE_URL}${item.imageUrl}`}
                  />
                  <Box sx={{ p: 1 }}>
                    <Typography variant="body2" noWrap>
                      {item.title || 'Без назви'}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        mt: 0.5,
                      }}
                    >
                      {item.Tags?.map((t) => (
                        <Box
                          key={t.tagId}
                          sx={{
                            px: 1,
                            py: 0.2,
                            borderRadius: 2,
                            bgcolor: t.color,
                          }}
                        >
                          <Typography variant="caption">{t.name}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <CardActions>
                    <Button
                      size="small"
                      onClick={() => handleSelectMedia(item)}
                    >
                      Вибрати
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[8, 12, 24, 48]}
          />
        </Box>
      </Modal>
    </Box>
  );
}
